import { useNamespaceCopy } from '@/i18n/ns';
import connectionsCopy from '@/i18n/ns/connections';
import { CopyLine } from './copy-line';
import { addressWarning, SAMPLE_QUESTION } from './format';
import type { AgentAddress } from './types';

/**
 * The handoff contains connection facts only; never a grant or an access key.
 * It is written for the coding agent, so it stays English in every interface language.
 */
function setupPrompt(address: AgentAddress) {
  const warning = addressWarning(address);
  return [
    'Help me connect this coding agent to Poneva so it can read my company’s approved knowledge.',
    `MCP server name: poneva\nMCP server URL: ${address.url}\nTransport: Streamable HTTP`,
    ...(warning ? [`Address availability: ${warning}`] : []),
    'Use this agent’s supported MCP setup commands or configuration format. Check the existing configuration first, preserve other servers, and prefer personal/user settings. If you cannot configure it yourself, give me the exact steps for this client.',
    'Use OAuth sign-in if supported. Open the sign-in flow and let me check the company and requested access in Poneva before I choose Allow. Start with read-only knowledge access; leave optional permissions off.',
    'If OAuth is unavailable or fails, guide me to Connections → Connected agents in Poneva to create an access key. Tell me where to enter it in the client’s secure settings (Authorization: Bearer <key> if it needs a header). Do not ask me to paste a key into this conversation or put one in source control.',
    `After setup, tell me if I need to reconnect or restart the client. Then use Poneva to answer: “${SAMPLE_QUESTION}” Report whether a knowledge read succeeded; if it did not, explain what is still needed.`,
  ].join('\n\n');
}

export function SetupPrompt({ address }: { address: AgentAddress }) {
  const copy = useNamespaceCopy(connectionsCopy);
  return (
    <div className="ws-agent-prompt">
      <h4>{copy.setupPromptTitle}</h4>
      <p>{copy.setupPromptBody}</p>
      <CopyLine value={setupPrompt(address)} label={copy.copyWhatPrompt} kind="prompt" />
    </div>
  );
}
